import { z } from 'zod';

/** A proportion carries what it was computed from, so a rate never appears
 *  without its population and a zero denominator reads as unavailable. */
export const partnerRate = z.object({
  numerator: z.number(),
  denominator: z.number(),
  denominatorMeaning: z.string(),
  available: z.boolean(),
});
export type PartnerRate = z.infer<typeof partnerRate>;

export const partnerCandidateRow = z.object({
  id: z.number(),
  saleId: z.string(),
  domain: z.string(),
  tld: z.string(),
  state: z.string(),
  firstSeenAt: z.string(),

  snapshotStartPriceCents: z.number(),
  snapshotRenewPriceCents: z.number().nullable(),
  renewalSource: z.string(),
  impliedMonthlyCents: z.number(),
  capCents: z.number().nullable(),
  requiredFirstBidCents: z.number().nullable(),
  economicallyViable: z.boolean(),
  nearRenewalPrice: z.boolean(),

  workingCapCents: z.number().nullable(),
  manualMaxCents: z.number().nullable(),
  liveBidCount: z.number().nullable(),
  livePriceCents: z.number().nullable(),
  liveRenewPriceCents: z.number().nullable(),
  liveEndDate: z.string().nullable(),
  secondsToEnd: z.number().nullable(),

  blocks: z.array(z.string()).nullable(),
});
export type PartnerCandidateRow = z.infer<typeof partnerCandidateRow>;

export const partnerDashboardHeader = z.object({
  killSwitchActive: z.boolean(),
  dryRun: z.boolean(),
  committedCents: z.number(),
  reservedCents: z.number(),
  budgetCents: z.number(),
  confirmedWins: z.number(),
  liveReady: z.boolean(),
  readinessBlockers: z.array(z.string()).nullable(),
  feedUrl: z.string(),
  feedColumns: z.number(),
  configVersion: z.number(),
});
export type PartnerDashboardHeader = z.infer<typeof partnerDashboardHeader>;

export const partnerReport = z.object({
  generatedAt: z.string(),
  eligibleCandidates: z.number(),
  watchedCandidates: z.number(),
  confirmedWins: z.number(),
  unviableRate: partnerRate,
  belowRevenueFloorRate: partnerRate,
  competitionSkippedRate: partnerRate,
  winRate: partnerRate,
  errorRate: partnerRate,
  budgetUtilization: partnerRate,
  wouldBidEvents: z.number(),
  bidsPlaced: z.number(),
  pendingOutcome: z.number(),
  committedCents: z.number(),
  reservedCents: z.number(),
  budgetCents: z.number(),
  aggregateMultiple: partnerRate,
  meanMultipleParts: z.array(partnerRate).nullable(),
});
export type PartnerReport = z.infer<typeof partnerReport>;

export const partnerDashboard = z.object({
  header: partnerDashboardHeader,
  candidates: z.array(partnerCandidateRow).nullable(),
  report: partnerReport.nullable(),
});
export type PartnerDashboard = z.infer<typeof partnerDashboard>;

/** Money is integer cents on the wire. Formatting is display only and never
 *  feeds a decision. */
export function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const s = `${Math.floor(abs / 100).toLocaleString()}.${String(abs % 100).padStart(2, '0')}`;
  return negative ? `-$${s}` : `$${s}`;
}

/** A rate is rendered with its own denominator, and an unavailable one says so
 *  rather than showing a misleading zero. */
export function formatRate(rate: PartnerRate | undefined): string {
  if (!rate || !rate.available) return 'unavailable';
  const pct = (rate.numerator / rate.denominator) * 100;
  return `${pct.toFixed(1)}% (${rate.numerator}/${rate.denominator})`;
}

export function formatMultiple(rate: PartnerRate | undefined): string {
  if (!rate || !rate.available) return 'unavailable';
  return `${(rate.numerator / rate.denominator).toFixed(2)}x`;
}

export function formatCountdown(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds <= 0) return 'ended';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** Matches the shared StatusTag tones. */
export type StateTone = 'neutral' | 'accent' | 'success' | 'warn' | 'danger';

/** Tone for each lifecycle state, so the table reads at a glance. */
export function stateTone(state: string): StateTone {
  switch (state) {
    case 'won':
      return 'success';
    case 'bid_placed':
    case 'snipe_window':
      return 'accent';
    case 'watching':
    case 'discovered':
      return 'neutral';
    case 'error':
      return 'danger';
    default:
      return 'warn';
  }
}

export const stateLabels: Record<string, string> = {
  discovered: 'Discovered',
  watching: 'Watching',
  snipe_window: 'Snipe window',
  bid_placed: 'Bid placed',
  won: 'Won',
  lost: 'Lost',
  skipped_competition: 'Skipped (competition)',
  unviable_economics: 'Unviable',
  expired_no_action: 'Expired',
  error: 'Error',
};

export const blockLabels: Record<string, string> = {
  duplicate_of_manual_queue: 'In curated queue',
  manual_exclusion: 'Excluded',
  below_min_implied_revenue: 'Below revenue floor',
  missing_renewal_cost: 'No renewal cost',
  untrusted_end_time: 'Untrusted end time',
  kill_switch_active: 'Kill switch',
  dry_run: 'Dry run',
  experiment_live_window_closed: 'Window closed',
  budget_blocked: 'Budget full',
  configuration_incomplete: 'Config incomplete',
  manual_max_below_opening: 'Max below opening bid',
  live_renewal_above_ceiling: 'Live renewal too high',
  feed_header_drift: 'Feed header drift',
};

/** The headroom a candidate has over its required first bid. This is the
 *  number that decides viability, so it is worth showing directly. */
export function headroomCents(row: PartnerCandidateRow): number | null {
  if (row.capCents === null || row.requiredFirstBidCents === null) return null;
  return row.capCents - row.requiredFirstBidCents;
}
